import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import {
  Box,
  Button,
  FormControl,
  Modal,
  Paper,
  Radio,
  RadioGroup,
  Typography,
} from '@mui/material';
import '../../land-visualization-modal.scss';
import { LazyLoadImage } from 'react-lazy-load-image-component';
import { HomeModelsResponseData } from '../../../../models/homes.models.tsx';
import { useState, Fragment } from 'react';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useGetHomeModels } from '../../../../api/homes.ts';
import { devOnlyCloudinaryInstance } from '../../../../utils/images.tsx';

interface HomeSelectListProps {
  handleChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  selectedHomeModel: string;
  setSelectedHomeObject: (val: HomeModelsResponseData) => void;
}

export const HomeSelectList = ({
  handleChange,
  selectedHomeModel,
  setSelectedHomeObject,
}: HomeSelectListProps) => {
  const theme = useTheme();

  const [openList, setOpenList] = useState(false);
  const [imageModalPath, setImageModalPath] = useState('');
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const maxHeight = isMobile ? '70px' : '95px';

  const getPriceRangeText = (priceMin: string, priceMax: string) => {
    let text = 'From';

    if (priceMin) text += ` $${Number(priceMin).toLocaleString()}`;

    if (priceMax) text += ` - $${Number(priceMax).toLocaleString()}`;

    return text;
  };
  const { data: HomeModelsData, loading, error } = useGetHomeModels();

  const selectHome = () => {
    const foundModel = HomeModelsData?.find(
      (item) => String(item.id) === selectedHomeModel
    );
    if (foundModel) {
      setSelectedHomeObject(foundModel);
      setOpenList(false);
    }
  };

  return (
    <>
      <Box className="select-house-modal-container">
        <Box>
          <Button onClick={() => setOpenList(!openList)}>
            <Typography
              sx={{
                fontSize: '12px',
              }}
            >
              Select Home
            </Typography>
          </Button>
        </Box>

        {openList && (
          <Paper elevation={0} className="select-house-modal">
            <Box className="select-house-list-container">
              <Box
                sx={{ padding: '10px', justifyItems: 'center', margin: 'auto' }}
              >
                <Typography sx={{ fontWeight: 800 }}>
                  Select Model to Visualize
                </Typography>
              </Box>
              <Box>
                <FormControl>
                  <RadioGroup defaultValue="0">
                    {loading && <Typography>Loading Homes Data...</Typography>}
                    {error && (
                      <Typography>
                        Something went wrong. Please try again later.
                      </Typography>
                    )}

                    {HomeModelsData &&
                      HomeModelsData.map(
                        (result: HomeModelsResponseData | undefined) => (
                          <Fragment key={result?.id}>
                            <Box
                              key={`${result?.id}-divider`}
                              className="divider"
                            ></Box>
                            <Box
                              key={result?.id}
                              className="select-house-item-container"
                            >
                              <LazyLoadImage
                                key={`image-${result?.id || ''}`}
                                alt="property"
                                effect="blur"
                                src={
                                  devOnlyCloudinaryInstance
                                    .image(result?.images[0]?.home_image_id)
                                    .toURL() || undefined
                                }
                                width="100px"
                                height={maxHeight}
                                style={{
                                  objectFit: 'cover',
                                }}
                                useIntersectionObserver={true}
                                onClick={() => {
                                  setImageModalPath(
                                    devOnlyCloudinaryInstance
                                      .image(result?.images[0]?.home_image_id)
                                      .toURL() || ''
                                  );
                                }}
                              />
                              <Box
                                sx={{
                                  paddingLeft: '10px',
                                  alignContent: 'center',
                                }}
                              >
                                <Typography>{result?.name || ''}</Typography>
                                <Typography
                                  sx={{
                                    color: '#757575',
                                    fontSize: '0.75rem',
                                  }}
                                >
                                  {getPriceRangeText(
                                    result?.price_min || '',
                                    result?.price_max || ''
                                  )}
                                </Typography>
                              </Box>
                              <Box sx={{ alignContent: 'center' }}>
                                <Radio
                                  checked={
                                    selectedHomeModel === String(result?.id)
                                  }
                                  onChange={handleChange}
                                  value={String(result?.id) || ''}
                                  name="radio-buttons"
                                  inputProps={{
                                    'aria-label': String(result?.id) || '',
                                  }}
                                />
                              </Box>
                            </Box>
                          </Fragment>
                        )
                      )}
                  </RadioGroup>
                </FormControl>
              </Box>
            </Box>
            <Box className="select-house-button-container">
              <Button onClick={selectHome} variant="contained">
                Visualize and Place Home
              </Button>
            </Box>
          </Paper>
        )}
      </Box>
      <Modal
        open={!!imageModalPath}
        sx={{ alignContent: 'center', justifyItems: 'center' }}
      >
        <Box sx={{ position: 'relative' }}>
          <Button
            sx={{ position: 'absolute', top: 6, zIndex: 100 }}
            onClick={() => setImageModalPath('')}
          >
            <ArrowBackIcon sx={{ color: 'white' }} />
          </Button>
          <LazyLoadImage
            alt="property"
            effect="blur"
            src={imageModalPath}
            width="100%"
            height={isMobile ? '80%' : '550px'}
            style={{
              objectFit: 'cover',
              borderRadius: '12px',
            }}
            useIntersectionObserver={true}
          />
        </Box>
      </Modal>
    </>
  );
};
